import { createEffect, createMemo, createSignal, Index, on, Show } from "solid-js";
import Text from "../../components/Text";
import Modal from "../../components/Modal";
import ItemStackView from "../../components/ItemStackView";
import QuantityInput from "../../components/QuantityInput";
import StorageSelect from "../../components/StorageSelect";
import { SearchInput } from "../../components/SearchInput";
import { Badge, type BadgeTone } from "../../components/Badge";
import { Pagination } from "../../components/Pagination";
import { EmptyState } from "../../components/EmptyState";
import { Skeleton } from "../../components/Skeleton";
import { BulkActionBar } from "../../components/BulkActionBar";
import FeatureGate from "../../components/FeatureGate";
import { showConfirm } from "../../components/Dialog";
import { useToast } from "../../components/MessageBox";
import { useItemName } from "../../hooks/useItemName";
import { useTranslation } from "../../hooks/useTranslation";
import { useTablePref } from "../../hooks/useTablePrefs";
import { useVisibilityPoll } from "../../hooks/useVisibilityPoll";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { fromStockRow } from "../../services/itemRefAdapters";
import { formatQuantity } from "../../services/quantityFormatter";
import { STORAGE_ACTIONS } from "../../services/resourceActions";
import { useInventoryApi, type PlannedOrder, type StockPolicyRequest, type StockRow, type StorageInfo } from "../../services/inventoryApi";
import { StockStatus } from "../../api/generated/schemas";
import StoragePlannedOrders from "./StoragePlannedOrders";
import shared from "./storageShared.module.css";
import styles from "./StorageStockTab.module.css";

const PAGE_SIZE = 50;
const DEFAULT_MIN_FACTOR = 0.5;
const DEFAULT_TARGET_FACTOR = 1.5;

type StockFilter = "all" | "attention" | "unmanaged";
type BulkMode = "fixed" | "factor";

interface Levels {
  minQuantity: number;
  targetQuantity: number;
}

const STATUS_BADGES: Record<StockStatus, { tone: BadgeTone; label: string }> = {
  [StockStatus.NUMBER_0]: { tone: "success", label: "STORAGE_STOCK_STATUS_OK" },
  [StockStatus.NUMBER_1]: { tone: "warning", label: "STORAGE_STOCK_STATUS_LOW" },
  [StockStatus.NUMBER_2]: { tone: "error", label: "STORAGE_STOCK_STATUS_EMPTY" },
};

const levelsFromOnHand = (onHand: number, minFactor: number, targetFactor: number): Levels => ({
  minQuantity: Math.round(onHand * minFactor),
  targetQuantity: Math.round(onHand * targetFactor),
});

const levelsValid = (levels: Levels) =>
  levels.minQuantity >= 0 && levels.targetQuantity > 0 && levels.targetQuantity >= levels.minQuantity;

interface Props {
  storage: StorageInfo;
  storages: StorageInfo[];
}

export default function StorageStockTab(props: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const inv = useInventoryApi();
  const { itemName, stackSize } = useItemName();

  const [rows, setRows] = createSignal<StockRow[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [plannedOrders, setPlannedOrders] = createSignal<PlannedOrder[]>([]);
  const [plannedLoading, setPlannedLoading] = createSignal(true);
  const craftingEnabled = useFeatureFlag("CRAFTING");
  const craftOrdersEnabled = useFeatureFlag("CRAFT_ORDERS");
  const plannedOrdersEnabled = () => craftingEnabled() && craftOrdersEnabled();
  const [saving, setSaving] = createSignal(false);
  const [search, setSearch] = createSignal("");
  const [filter, setFilter] = useTablePref<StockFilter>("storageStock.filter", "all");
  const [page, setPage] = createSignal(0);

  const [selected, setSelected] = createSignal<Set<string>>(new Set());
  const [allMatchingSelected, setAllMatchingSelected] = createSignal(false);

  const [editingKey, setEditingKey] = createSignal<string | null>(null);
  const [draftMin, setDraftMin] = createSignal(0);
  const [draftTarget, setDraftTarget] = createSignal(0);
  const [draftSourceId, setDraftSourceId] = createSignal("");

  const [bulkOpen, setBulkOpen] = createSignal(false);
  const [bulkMode, setBulkMode] = createSignal<BulkMode>("fixed");
  const [bulkMin, setBulkMin] = createSignal(0);
  const [bulkTarget, setBulkTarget] = createSignal(0);
  const [bulkMinFactor, setBulkMinFactor] = createSignal(DEFAULT_MIN_FACTOR);
  const [bulkTargetFactor, setBulkTargetFactor] = createSignal(DEFAULT_TARGET_FACTOR);

  const mayWrite = () => props.storage.myActions.includes(STORAGE_ACTIONS.ItemsWrite);
  const otherStorages = createMemo(() => props.storages.filter(storage => storage.id !== props.storage.id));
  const stackSizeOf = (row: StockRow) => stackSize(`${row.itemId}:${row.metadata}`) ?? 64;

  async function loadStock(initial: boolean) {
    const storageId = props.storage.id;
    try {
      const stockRows = await inv.fetchStock(storageId);
      if (storageId === props.storage.id) setRows(stockRows);
    } catch (e: any) {
      if (initial) toast?.error(e?.message ?? t("STORAGE_STOCK_LOAD_FAILED"));
    } finally {
      if (initial) setLoading(false);
    }
  }

  async function loadPlannedOrders(initial: boolean) {
    if (!plannedOrdersEnabled()) return;
    const storageId = props.storage.id;
    try {
      const orders = await inv.fetchPlannedOrders(storageId);
      if (storageId === props.storage.id) setPlannedOrders(orders);
    } catch (e: any) {
      if (initial) toast?.error(e?.message ?? t("STORAGE_PLANNED_LOAD_FAILED"));
    } finally {
      if (initial) setPlannedLoading(false);
    }
  }

  async function load(initial: boolean) {
    await Promise.all([loadStock(initial), loadPlannedOrders(initial)]);
  }

  createEffect(on(() => props.storage.id, () => {
    setRows([]);
    setLoading(true);
    setEditingKey(null);
    clearSelection();
    void loadStock(true);
  }));

  // The flags can arrive after the tab mounts on a first login, so they trigger the load as well.
  createEffect(on([() => props.storage.id, plannedOrdersEnabled], () => {
    setPlannedOrders([]);
    setPlannedLoading(true);
    void loadPlannedOrders(true);
  }));

  // A refresh mid-edit could reorder the rows under the open inputs, so it waits for the edit to end.
  // Planned orders are not polled: one plan of a storage with hundreds of low shelves takes seconds
  // of server time, so they reload on a save, a submit, or the card's own refresh button.
  useVisibilityPoll(() => {
    if (editingKey() === null && !saving() && !bulkOpen()) void loadStock(false);
  }, 30_000);

  createEffect(on([search, filter], () => setPage(0), { defer: true }));

  const matchingRows = createMemo(() => {
    const query = search().trim().toLowerCase();
    return rows().filter(row => {
      if (filter() === "attention" && (!row.policy || row.policy.status === StockStatus.NUMBER_0)) return false;
      if (filter() === "unmanaged" && row.policy) return false;
      return !query || itemName(row.itemKey, row.snbt).toLowerCase().includes(query);
    });
  });
  const pageRows = createMemo(() => matchingRows().slice(page() * PAGE_SIZE, (page() + 1) * PAGE_SIZE));
  const totalPages = () => Math.ceil(matchingRows().length / PAGE_SIZE);

  const selectedRows = createMemo(() => allMatchingSelected()
    ? matchingRows()
    : rows().filter(row => selected().has(row.itemKey)));
  const selectedPolicyKeys = () => selectedRows().filter(row => row.policy).map(row => row.itemKey);

  function clearSelection() {
    setSelected(new Set<string>());
    setAllMatchingSelected(false);
  }

  function toggleRow(itemKey: string) {
    setAllMatchingSelected(false);
    setSelected(previous => {
      const next = new Set<string>(previous);
      if (next.has(itemKey)) next.delete(itemKey);
      else next.add(itemKey);
      return next;
    });
  }

  function togglePage() {
    const pageKeys = pageRows().map(row => row.itemKey);
    const allOnPage = pageKeys.length > 0 && pageKeys.every(key => selected().has(key));
    setAllMatchingSelected(false);
    setSelected(previous => {
      const next = new Set<string>(previous);
      for (const key of pageKeys) {
        if (allOnPage) next.delete(key);
        else next.add(key);
      }
      return next;
    });
  }

  async function save(upserts: StockPolicyRequest[], removedItemKeys: string[], successMessage: string): Promise<boolean> {
    setSaving(true);
    try {
      setRows(await inv.saveStock(props.storage.id, upserts, removedItemKeys));
      toast?.success(successMessage);
      void loadPlannedOrders(false);
      return true;
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_STOCK_SAVE_FAILED"));
      return false;
    } finally {
      setSaving(false);
    }
  }

  function startEdit(row: StockRow) {
    const suggested = levelsFromOnHand(row.onHand, DEFAULT_MIN_FACTOR, DEFAULT_TARGET_FACTOR);
    setDraftMin(row.policy?.minQuantity ?? suggested.minQuantity);
    setDraftTarget(row.policy?.targetQuantity ?? Math.max(suggested.targetQuantity, stackSizeOf(row)));
    setDraftSourceId(row.policy?.sourceStorageId ?? "");
    setEditingKey(row.itemKey);
  }

  const draftValid = () => levelsValid({ minQuantity: draftMin(), targetQuantity: draftTarget() });

  async function saveEdit(row: StockRow) {
    const saved = await save([{
      itemKey: row.itemKey,
      minQuantity: draftMin(),
      targetQuantity: draftTarget(),
      sourceStorageId: draftSourceId() || null,
    }], [], t("STORAGE_STOCK_SAVED"));
    if (saved) setEditingKey(null);
  }

  const bulkStackSize = () => {
    const stackSizes = new Set(selectedRows().map(stackSizeOf));
    return stackSizes.size === 1 ? [...stackSizes][0] : undefined;
  };

  function openBulk() {
    const commonStackSize = bulkStackSize() ?? 64;
    setBulkMin(commonStackSize);
    setBulkTarget(2 * commonStackSize);
    setBulkOpen(true);
  }

  const bulkFactorsValid = () =>
    Number.isFinite(bulkMinFactor()) && Number.isFinite(bulkTargetFactor())
    && bulkMinFactor() >= 0 && bulkTargetFactor() > 0 && bulkTargetFactor() >= bulkMinFactor();

  const bulkInputsValid = () => bulkMode() === "fixed"
    ? levelsValid({ minQuantity: bulkMin(), targetQuantity: bulkTarget() })
    : bulkFactorsValid();

  const bulkPreview = createMemo(() => selectedRows().map(row => {
    const levels = bulkMode() === "fixed"
      ? { minQuantity: bulkMin(), targetQuantity: bulkTarget() }
      : levelsFromOnHand(row.onHand, bulkMinFactor(), bulkTargetFactor());
    return { row, levels, valid: levelsValid(levels) };
  }));
  const bulkUpserts = () => (bulkInputsValid() ? bulkPreview() : []).filter(preview => preview.valid).map((preview): StockPolicyRequest => ({
    itemKey: preview.row.itemKey,
    ...preview.levels,
    sourceStorageId: preview.row.policy?.sourceStorageId ?? null,
  }));

  async function applyBulk() {
    const upserts = bulkUpserts();
    if (!await save(upserts, [], t("STORAGE_STOCK_BULK_SAVED", upserts.length))) return;
    setBulkOpen(false);
    clearSelection();
  }

  async function removeSelected() {
    const itemKeys = selectedPolicyKeys();
    if (!await showConfirm(t("STORAGE_STOCK_REMOVE_CONFIRM", itemKeys.length), true)) return;
    if (await save([], itemKeys, t("STORAGE_STOCK_REMOVED", itemKeys.length))) clearSelection();
  }

  const sourceName = (row: StockRow) => {
    const sourceId = row.policy?.sourceStorageId;
    if (!sourceId) return "";
    return props.storages.find(storage => storage.id === sourceId)?.name ?? t("STORAGE_STOCK_SOURCE_HIDDEN");
  };

  const parseFactor = (raw: string) => (raw.trim() === "" ? NaN : Number(raw));

  const FilterPill = (pillProps: { value: StockFilter; label: string }) => (
    <button
      class={`${shared.pill} ${filter() === pillProps.value ? shared.pillOn : ""}`}
      onClick={() => setFilter(pillProps.value)}
    >
      <Text val={pillProps.label} />
    </button>
  );

  return (
    <>
      <FeatureGate flag="CRAFTING">
        <FeatureGate flag="CRAFT_ORDERS">
          <StoragePlannedOrders
            storage={props.storage}
            orders={plannedOrders()}
            loading={plannedLoading()}
            onRefresh={() => loadPlannedOrders(false)}
            onSubmitted={() => load(false)}
          />
        </FeatureGate>
      </FeatureGate>

      <div class={shared.toolbar}>
        <div class={styles.search}>
          <SearchInput value={search()} onInput={setSearch} placeholder={t("STORAGE_STOCK_SEARCH")} ariaLabel={t("STORAGE_STOCK_SEARCH")} />
        </div>
        <div class={shared.pills}>
          <FilterPill value="all" label="STORAGE_FILTER_ALL" />
          <FilterPill value="attention" label="STORAGE_STOCK_FILTER_ATTENTION" />
          <FilterPill value="unmanaged" label="STORAGE_STOCK_FILTER_UNMANAGED" />
        </div>
        <Show when={!loading()}>
          <span class={styles.count}>{t("STORAGE_STOCK_COUNT", matchingRows().length)}</span>
        </Show>
      </div>

      <Show when={!loading()} fallback={<Skeleton variant="rect" height="20rem" />}>
        <Show when={rows().length > 0} fallback={
          <EmptyState icon="fa-boxes-stacked" title={t("STORAGE_STOCK_EMPTY")} hint={t("STORAGE_STOCK_EMPTY_HINT")} />
        }>
          <Show when={matchingRows().length > 0} fallback={
            <EmptyState
              icon="fa-filter-circle-xmark"
              title={t("STORAGE_STOCK_NO_MATCH")}
              action={
                <button class="btn btn-secondary btn-sm" onClick={() => { setSearch(""); setFilter("all"); }}>
                  <Text val="STORAGE_FILTER_ALL" />
                </button>
              }
            />
          }>
            <div class={shared.tableWrap}>
              <table class={shared.table}>
                <thead>
                  <tr>
                    <Show when={mayWrite()}>
                      <th class={shared.thSelect}>
                        <input
                          type="checkbox"
                          aria-label={t("SELECTALL")}
                          checked={pageRows().length > 0 && pageRows().every(row => selected().has(row.itemKey))}
                          onChange={togglePage}
                        />
                      </th>
                    </Show>
                    <th><Text val="STORAGE_STOCK_COL_ITEM" /></th>
                    <th><Text val="STORAGE_STOCK_COL_STATUS" /></th>
                    <th class={shared.thRight}><Text val="STORAGE_STOCK_COL_ON_HAND" /></th>
                    <th class={shared.thRight}><Text val="STORAGE_STOCK_COL_MIN" /></th>
                    <th class={shared.thRight}><Text val="STORAGE_STOCK_COL_TARGET" /></th>
                    <th><Text val="STORAGE_STOCK_COL_SOURCE" /></th>
                    <th class={shared.thRight}><Text val="STORAGE_STOCK_COL_OPEN_SUPPLY" /></th>
                    <Show when={mayWrite()}>
                      <th class={shared.thRight}><Text val="STORAGE_COL_ACTIONS" /></th>
                    </Show>
                  </tr>
                </thead>
                <tbody>
                  <Index each={pageRows()}>
                    {(row) => {
                      const editing = () => editingKey() === row().itemKey;
                      const isSelected = () => allMatchingSelected() || selected().has(row().itemKey);
                      return (
                        <tr class={isSelected() ? shared.rowSelected : ""}>
                          <Show when={mayWrite()}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={itemName(row().itemKey, row().snbt)}
                                checked={isSelected()}
                                onChange={() => toggleRow(row().itemKey)}
                              />
                            </td>
                          </Show>
                          <td>
                            <div class={styles.itemCell}>
                              <ItemStackView stack={fromStockRow(row())} size={24} bare showTooltip />
                              <span class={styles.itemName}>{itemName(row().itemKey, row().snbt)}</span>
                            </div>
                          </td>
                          <td>
                            <Show when={row().policy}>
                              <Badge tone={STATUS_BADGES[row().policy!.status].tone}>
                                <Text val={STATUS_BADGES[row().policy!.status].label} />
                              </Badge>
                            </Show>
                          </td>
                          <td class={styles.number}>{formatQuantity(row().onHand, stackSizeOf(row()))}</td>
                          <Show when={editing()} fallback={
                            <>
                              <td class={styles.number}>
                                <Show when={row().policy} fallback={<span class={styles.muted}>-</span>}>
                                  {formatQuantity(row().policy!.minQuantity, stackSizeOf(row()))}
                                </Show>
                              </td>
                              <td class={styles.number}>
                                <Show when={row().policy} fallback={<span class={styles.muted}>-</span>}>
                                  {formatQuantity(row().policy!.targetQuantity, stackSizeOf(row()))}
                                </Show>
                              </td>
                              <td class={styles.source}>{sourceName(row())}</td>
                            </>
                          }>
                            <td class={styles.editCell}>
                              <QuantityInput inline presets={false} value={draftMin()} min={0} stackSize={stackSizeOf(row())} onChange={setDraftMin} />
                            </td>
                            <td class={styles.editCell}>
                              <QuantityInput inline presets={false} value={draftTarget()} min={1} stackSize={stackSizeOf(row())} onChange={setDraftTarget} />
                            </td>
                            <td class={styles.sourceEditCell}>
                              <StorageSelect
                                storages={otherStorages()}
                                value={draftSourceId()}
                                onChange={setDraftSourceId}
                                emptyOption={{ label: t("STORAGE_STOCK_SOURCE_SELF"), icon: "fa-box" }}
                              />
                            </td>
                          </Show>
                          <td class={styles.number}>
                            <Show when={row().openSupply > 0} fallback={<span class={styles.muted}>-</span>}>
                              {formatQuantity(row().openSupply, stackSizeOf(row()))}
                            </Show>
                          </td>
                          <Show when={mayWrite()}>
                            <td>
                              <div class={shared.rowActions}>
                                <Show when={editing()} fallback={
                                  <button
                                    class={shared.iconBtn}
                                    title={t(row().policy ? "STORAGE_STOCK_EDIT" : "STORAGE_STOCK_SET_LEVELS")}
                                    onClick={() => startEdit(row())}
                                  >
                                    <i class={`fa-solid ${row().policy ? "fa-pen-to-square" : "fa-sliders"}`} />
                                  </button>
                                }>
                                  <button
                                    class={shared.iconBtn}
                                    title={t("SAVE")}
                                    disabled={!draftValid() || saving()}
                                    onClick={() => void saveEdit(row())}
                                  >
                                    <i class={`fa-solid ${saving() ? "fa-spinner fa-spin" : "fa-check"}`} />
                                  </button>
                                  <button class={shared.iconBtn} title={t("CANCEL")} onClick={() => setEditingKey(null)}>
                                    <i class="fa-solid fa-xmark" />
                                  </button>
                                </Show>
                              </div>
                            </td>
                          </Show>
                        </tr>
                      );
                    }}
                  </Index>
                </tbody>
              </table>
            </div>

            <Show when={totalPages() > 1}>
              <div class={shared.pager}>
                <Pagination page={page()} totalPages={totalPages()} onChange={setPage} size="sm" />
              </div>
            </Show>

            <BulkActionBar
              selectedCount={selectedRows().length}
              matchingCount={matchingRows().length}
              allMatchingSelected={allMatchingSelected()}
              onSelectAllMatching={() => setAllMatchingSelected(true)}
              onClear={clearSelection}
              actions={
                <>
                  <button class="btn btn-secondary btn-sm" onClick={openBulk}>
                    <i class="fa-solid fa-sliders" /> <Text val="STORAGE_STOCK_SET_LEVELS" />
                  </button>
                  <button
                    class="btn btn-danger btn-sm"
                    disabled={selectedPolicyKeys().length === 0 || saving()}
                    onClick={() => void removeSelected()}
                  >
                    <i class="fa-solid fa-trash" /> <Text val="STORAGE_STOCK_REMOVE" />
                  </button>
                </>
              }
            />
          </Show>
        </Show>
      </Show>

      <Modal
        open={bulkOpen()}
        onClose={() => setBulkOpen(false)}
        title={<Text val="STORAGE_STOCK_SET_LEVELS" />}
        subtitle={t("BULK_SELECTED", selectedRows().length)}
        leading={<i class="fa-solid fa-sliders" />}
        maxWidth="56rem"
        busy={saving()}
      >
        <form class="v" style={{ gap: "var(--spacing-md)" }} onSubmit={(e) => { e.preventDefault(); void applyBulk(); }}>
          <div class={shared.pills}>
            <button type="button" class={`${shared.pill} ${bulkMode() === "fixed" ? shared.pillOn : ""}`} onClick={() => setBulkMode("fixed")}>
              <Text val="STORAGE_STOCK_MODE_FIXED" />
            </button>
            <button type="button" class={`${shared.pill} ${bulkMode() === "factor" ? shared.pillOn : ""}`} onClick={() => setBulkMode("factor")}>
              <Text val="STORAGE_STOCK_MODE_FACTOR" />
            </button>
          </div>

          <Show when={bulkMode() === "fixed"} fallback={
            <>
              <div class={styles.levelInputs}>
                <label class="v" style={{ gap: "var(--spacing-xs)" }}>
                  <span class={shared.fieldLabel}><Text val="STORAGE_STOCK_MIN_FACTOR" /></span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={bulkMinFactor()}
                    onInput={(e) => setBulkMinFactor(parseFactor(e.currentTarget.value))}
                  />
                </label>
                <label class="v" style={{ gap: "var(--spacing-xs)" }}>
                  <span class={shared.fieldLabel}><Text val="STORAGE_STOCK_TARGET_FACTOR" /></span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={bulkTargetFactor()}
                    onInput={(e) => setBulkTargetFactor(parseFactor(e.currentTarget.value))}
                  />
                </label>
              </div>
              <span class={shared.fieldHint}><Text val="STORAGE_STOCK_FACTOR_HINT" /></span>
            </>
          }>
            <div class={styles.levelInputs}>
              <label class="v" style={{ gap: "var(--spacing-xs)" }}>
                <span class={shared.fieldLabel}><Text val="STORAGE_STOCK_MIN" /></span>
                <QuantityInput value={bulkMin()} min={0} stackSize={bulkStackSize()} onChange={setBulkMin} />
              </label>
              <label class="v" style={{ gap: "var(--spacing-xs)" }}>
                <span class={shared.fieldLabel}><Text val="STORAGE_STOCK_TARGET" /></span>
                <QuantityInput value={bulkTarget()} min={1} stackSize={bulkStackSize()} onChange={setBulkTarget} />
              </label>
            </div>
          </Show>

          <Show when={!bulkInputsValid()}>
            <div class={shared.inlineError}><Text val="STORAGE_STOCK_LEVELS_INVALID" /></div>
          </Show>

          <div class={shared.sectionTitle}><Text val="STORAGE_STOCK_PREVIEW" /></div>
          <div class={`${shared.tableWrap} ${styles.previewWrap}`}>
            <table class={shared.table}>
              <thead>
                <tr>
                  <th><Text val="STORAGE_STOCK_COL_ITEM" /></th>
                  <th class={shared.thRight}><Text val="STORAGE_STOCK_COL_ON_HAND" /></th>
                  <th class={shared.thRight}><Text val="STORAGE_STOCK_MIN" /></th>
                  <th class={shared.thRight}><Text val="STORAGE_STOCK_TARGET" /></th>
                </tr>
              </thead>
              <tbody>
                <Index each={bulkPreview()}>
                  {(preview) => (
                    <tr>
                      <td>
                        <div class={styles.itemCell}>
                          <ItemStackView stack={fromStockRow(preview().row)} size={24} bare />
                          <span class={styles.itemName}>{itemName(preview().row.itemKey, preview().row.snbt)}</span>
                        </div>
                      </td>
                      <td class={styles.number}>{formatQuantity(preview().row.onHand, stackSizeOf(preview().row))}</td>
                      <Show when={bulkInputsValid() && preview().valid} fallback={
                        <td class={styles.skipped} colSpan={2}>
                          <Show when={bulkInputsValid()} fallback="-"><Text val="STORAGE_STOCK_BULK_SKIPPED" /></Show>
                        </td>
                      }>
                        <td class={styles.number}>{formatQuantity(preview().levels.minQuantity, stackSizeOf(preview().row))}</td>
                        <td class={styles.number}>{formatQuantity(preview().levels.targetQuantity, stackSizeOf(preview().row))}</td>
                      </Show>
                    </tr>
                  )}
                </Index>
              </tbody>
            </table>
          </div>

          <div class="h" style={{ "justify-content": "flex-end", gap: "var(--spacing-sm)" }}>
            <button type="button" class="btn btn-secondary" onClick={() => setBulkOpen(false)}><Text val="CANCEL" /></button>
            <button type="submit" class="btn btn-primary" disabled={saving() || bulkUpserts().length === 0}>
              <Show when={saving()}><i class="fa-solid fa-spinner fa-spin" /> </Show>
              <Text val="STORAGE_STOCK_APPLY" />
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
